import { forwardRef, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { DrugSearchResult } from "@/lib/api";
import { useDrugSearch } from "@/hooks/useDrugSearch";

interface DrugSearchInputProps {
  value: string;
  onChange: (drug: DrugSearchResult) => void;
  onCustomInput?: (value: string) => void;
  placeholder?: string;
  className?: string;
  onEnterNext?: () => void;
}

const DrugSearchInput = forwardRef<HTMLInputElement, DrugSearchInputProps>(
  ({ value, onChange, onCustomInput, placeholder = "Search drug...", className, onEnterNext }, ref) => {
    const [inputValue, setInputValue] = useState(value);
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

    const { drugs, isLoading } = useDrugSearch(inputValue);
    
    const handleCustomInput = (val: string) => {
      if (onCustomInput) {
        onCustomInput(val);
      }
    };

    useEffect(() => {
      setInputValue(value);
    }, [value]);

    useEffect(() => {
      setHighlightedIndex(0);
    }, [drugs]);

    useEffect(() => {
      if (isOpen && optionRefs.current[highlightedIndex] && dropdownRef.current) {
        const highlightedElement = optionRefs.current[highlightedIndex];
        const dropdown = dropdownRef.current;

        if (highlightedElement) {
          const dropdownScrollTop = dropdown.scrollTop;
          const dropdownHeight = dropdown.clientHeight;
          const elementOffsetTop = highlightedElement.offsetTop;
          const elementHeight = highlightedElement.offsetHeight;

          if (elementOffsetTop < dropdownScrollTop) {
            dropdown.scrollTop = elementOffsetTop;
          } else if (elementOffsetTop + elementHeight > dropdownScrollTop + dropdownHeight) {
            dropdown.scrollTop = elementOffsetTop + elementHeight - dropdownHeight;
          }
        }
      }
    }, [highlightedIndex, isOpen]);

    useEffect(() => {
      optionRefs.current = optionRefs.current.slice(0, drugs.length);
      if (highlightedIndex >= drugs.length && drugs.length > 0) {
        setHighlightedIndex(drugs.length - 1);
      }
    }, [drugs.length, highlightedIndex]);

    const handleSelect = (drug: DrugSearchResult, e: React.MouseEvent) => {
      e.preventDefault();
      setInputValue(drug.brand_name);
      onChange(drug);
      setIsOpen(false);
      if (onEnterNext) setTimeout(() => onEnterNext(), 100);
    };

    const showDropdown = isOpen && inputValue.trim().length >= 2;

    return (
      <div ref={containerRef} className="relative">
        <Input
          ref={ref}
          value={inputValue}
          onChange={(e) => {
            const val = e.target.value;
            setInputValue(val);
            handleCustomInput(val);
            if (val.trim().length >= 2) {
              setIsOpen(true);
            } else {
              setIsOpen(false);
            }
          }}
          onFocus={() => {
            if (inputValue && inputValue.trim().length >= 2) {
              setIsOpen(true);
            }
          }}
          onClick={() => {
            if (inputValue && inputValue.trim().length >= 2) {
              setIsOpen(true);
            }
          }}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          onKeyDown={(e) => {
            if (!isOpen && drugs.length > 0 && inputValue.trim().length >= 2) {
              setIsOpen(true);
              setHighlightedIndex(0);
              return;
            }

            if (!isOpen) {
              if (e.key === "Enter" && onEnterNext) {
                e.preventDefault();
                onEnterNext();
              }
              return;
            }

            if (e.key === "ArrowDown") {
              e.preventDefault();
              const newIndex = Math.min(highlightedIndex + 1, drugs.length - 1);
              setHighlightedIndex(newIndex);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              const newIndex = Math.max(highlightedIndex - 1, 0);
              setHighlightedIndex(newIndex);
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (drugs.length > 0 && highlightedIndex >= 0 && highlightedIndex < drugs.length) {
                const drug = drugs[highlightedIndex];
                setInputValue(drug.brand_name);
                onChange(drug);
                setIsOpen(false);
                if (onEnterNext) setTimeout(() => onEnterNext(), 100);
              } else if (onEnterNext) {
                onEnterNext();
              }
            } else if (e.key === "Escape") {
              setIsOpen(false);
            } else if (e.key === "Tab") {
              setIsOpen(false);
            }
          }}
          placeholder={placeholder}
          className={cn("h-8", className)}
          autoComplete="off"
        />

        {showDropdown && (
          <div
            ref={dropdownRef}
            className="absolute z-dropdown bg-popover shadow-lg border border-border rounded-md max-h-64 overflow-auto text-sm ring-1 ring-border focus:outline-none"
            style={{
              top: "100%",
              left: 0,
              marginTop: "4px",
              minWidth: "320px",
              width: "100%",
            }}
          >
            {isLoading && (
              <div className="px-3 py-4 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {!isLoading && drugs.length > 0 && (
              drugs.map((drug, i) => (
                <div
                  key={drug.id}
                  ref={(el) => {
                    optionRefs.current[i] = el;
                  }}
                  className={cn(
                    "px-3 py-2 cursor-pointer transition-colors first:rounded-t-md last:rounded-b-md",
                    i === highlightedIndex
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-accent text-foreground"
                  )}
                  onMouseEnter={() => setHighlightedIndex(i)}
                  onMouseDown={(e) => handleSelect(drug, e)}
                >
                  <div className="flex items-center gap-1.5">
                    {drug.is_favourite === true? (
                      <span className="text-yellow-500">★</span>
                    ) : null}
                    <span className="font-semibold text-sm truncate capitalize">
                      {drug.brand_name}
                    </span>
                  </div>
                  <div className={cn(
                    "text-xs mt-0.5",
                    i === highlightedIndex ? "text-primary-foreground/80" : "text-muted-foreground"
                  )}>
                    {drug.generic_name}
                  </div>
                </div>
              ))
            )}

            {!isLoading && drugs.length === 0 && (
              <div className="px-3 py-3 text-muted-foreground text-center italic">
                No results found
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

DrugSearchInput.displayName = "DrugSearchInput";
export default DrugSearchInput;
